import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { products, collections } from '../mock/mock';
import { ChevronDown, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import Reveal from '../components/Reveal';
import SmoothImage from '../components/SmoothImage';

const SORTS = [
  { id: 'featured', label: 'Featured' },
  { id: 'price_asc', label: 'Price: Low to High' },
  { id: 'price_desc', label: 'Price: High to Low' },
  { id: 'name', label: 'Name A-Z' },
];

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const collectionParam = params.get('collection');
  const categoryParam = params.get('category');
  const queryParam = params.get('q');

  const [sort, setSort] = useState('featured');
  const [sortOpen, setSortOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [collectionParam, categoryParam, queryParam]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (collectionParam && collectionParam !== 'all') {
      list = list.filter((p) => p.collection === collectionParam);
    }
    if (categoryParam) {
      list = list.filter((p) => p.category === categoryParam);
    }
    if (queryParam) {
      const q = queryParam.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
      );
    }
    switch (sort) {
      case 'price_asc': list.sort((a, b) => a.price - b.price); break;
      case 'price_desc': list.sort((a, b) => b.price - a.price); break;
      case 'name': list.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return list;
  }, [collectionParam, categoryParam, queryParam, sort]);

  const heading = useMemo(() => {
    if (queryParam) return `Results for “${queryParam}”`;
    if (collectionParam && collectionParam !== 'all') {
      const c = collections.find((x) => x.id === collectionParam);
      return c ? c.name : 'Collection';
    }
    if (categoryParam) {
      const cap = categoryParam.charAt(0).toUpperCase() + categoryParam.slice(1);
      return categoryParam === 'bottoms' ? 'Pants' : cap;
    }
    return 'All Pieces';
  }, [collectionParam, categoryParam, queryParam]);

  const categories = ['all', 'bottoms', 'swimwear', 'bags'];
  const categoryLabel = (c) => (c === 'bottoms' ? 'pants' : c);

  return (
    <div className="pt-32 pb-32">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Reveal variant="fade">
            <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-5">The Edit</p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight">{heading}</h1>
          </Reveal>
          <Reveal delay={240} variant="fade">
            <p className="mt-5 text-sm text-neutral-500">{filtered.length} pieces</p>
          </Reveal>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between border-y border-neutral-200 py-5 mb-12 gap-4">
          <div className="flex flex-wrap gap-1">
            {categories.map((c) => {
              const active = (c === 'all' && !categoryParam) || categoryParam === c;
              return (
                <button
                  key={c}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    if (c === 'all') next.delete('category');
                    else next.set('category', c);
                    setParams(next);
                  }}
                  className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase transition-colors ${
                    active ? 'text-neutral-900 underline underline-offset-8' : 'text-neutral-500 hover:text-neutral-900'
                  }`}
                >
                  {categoryLabel(c)}
                </button>
              );
            })}
          </div>

          <div className="relative">
            <button
              onClick={() => setSortOpen((o) => !o)}
              className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase"
            >
              Sort: {SORTS.find((s) => s.id === sort)?.label}
              <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-3 bg-white border border-neutral-200 shadow-lg min-w-[220px] z-20">
                {SORTS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setSort(s.id); setSortOpen(false); }}
                    className={`block w-full text-left px-5 py-3 text-sm hover:bg-neutral-50 ${
                      sort === s.id ? 'text-neutral-900' : 'text-neutral-500'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="py-32 text-center">
            <p className="font-display text-3xl mb-3">No pieces found</p>
            <p className="text-sm text-neutral-500 mb-8">Try clearing your filters.</p>
            <Link
              to="/shop"
              className="inline-block bg-neutral-900 text-white px-10 py-4 text-[11px] tracking-[0.22em] uppercase"
            >
              View All
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-10">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 100} duration={900}>
                <ShopCard product={p} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ShopCard = ({ product }) => {
  const { wishlist, toggleWishlist } = useCart();
  const isWishlisted = wishlist.includes(product.id);

  return (
    <Link to={`/product/${product.id}`} className="group block">
      <div className="relative aspect-[3/4] mb-5 overflow-hidden bg-neutral-100">
        <SmoothImage
          src={product.image}
          alt={product.name}
          className="w-full h-full"
        />
        {product.onSale && (
          <span className="absolute top-4 left-4 z-10 bg-white px-3 py-1 text-[10px] tracking-[0.2em] uppercase">
            Sale
          </span>
        )}
        <button
          onClick={(e) => { e.preventDefault(); toggleWishlist(product.id); }}
          className="absolute top-4 right-4 z-10 w-9 h-9 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors rounded-full opacity-0 group-hover:opacity-100"
          aria-label="Wishlist"
        >
          <Heart
            className="w-4 h-4"
            strokeWidth={1.5}
            fill={isWishlisted ? 'currentColor' : 'none'}
          />
        </button>
      </div>
      <h3 className="text-sm font-medium">{product.name}</h3>
      <div className="flex gap-2 text-sm text-neutral-700 mt-1">
        <span>${product.price}</span>
        {product.originalPrice && (
          <span className="line-through text-neutral-400">${product.originalPrice}</span>
        )}
      </div>
    </Link>
  );
};

export default Shop;
